import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  fetchAdminUploadDetail,
  fetchAdminUploadDiagnostics,
  adminTakeOverUpload,
} from "@/Service/AdminUploadMonitorService";
import { fetchActiveSessions } from "@/store/packagesSlice";
import { getUploadSessionId } from "@/utils/uploadSessionId";
import { HEALTH_LABEL, HEALTH_SEVERITY, deriveRowHealth } from "./adminUploadHealth";
import { isAdminTakeoverEligible } from "./adminTakeoverStatuses";

/**
 * Admin drawer showing the full observability picture for one upload:
 *   - Header row: uploadId, contributor, current health, "Open Session Replay"
 *   - Tabs:
 *       - Timeline         (ring buffer from the client-shipped snapshot)
 *       - Network events   (filtered subset of the timeline)
 *       - API requests     (filtered subset — api_request / api_response / api_error)
 *       - Retries          (filtered subset — retry_started / retry_completed / chunk_failed)
 *       - State summary    (uploader state, health inputs, throughput, environment)
 *       - Raw JSON         (whole snapshot for pasteable support tickets)
 *
 * Data sources:
 *   - GET /api/v1/admin/uploads/{uploadId}          (live server-side row)
 *   - GET /api/v1/admin/uploads/{uploadId}/diagnostics  (client snapshot)
 *
 * Both are fetched in parallel on open. Neither auto-refreshes — the drawer is
 * a triage tool, not a live monitor; the manual refresh button covers cases
 * where a new snapshot lands mid-investigation.
 */

const AdminUploadDiagnosticsDrawer = ({ open, uploadId, onClose, initialRow }) => {
  const dispatch = useDispatch();
  const [detail, setDetail] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  // Admin takeover state (2026-08-06). Peer to Open Session Replay in the
  // header — surfaces on the live diagnostics drawer that opens from the
  // /admin/uploads row, so an admin can reclaim a stranger's upload without
  // having to route through the outcome-tile drawer.
  const [takeOverOpen, setTakeOverOpen] = useState(false);
  const [takeOverBusy, setTakeOverBusy] = useState(false);
  const [takeOverError, setTakeOverError] = useState(null);
  const [takeOverBanner, setTakeOverBanner] = useState(null);

  const load = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    setError(null);
    try {
      const [detailData, diagData] = await Promise.allSettled([
        fetchAdminUploadDetail(uploadId),
        fetchAdminUploadDiagnostics(uploadId),
      ]);
      if (detailData.status === "fulfilled") setDetail(detailData.value);
      if (diagData.status === "fulfilled") setDiagnostics(diagData.value);
      const failure =
        detailData.status === "rejected"
          ? detailData.reason
          : diagData.status === "rejected"
          ? diagData.reason
          : null;
      if (failure) {
        setError(
          failure?.response?.data?.message || failure?.message || "Failed to load diagnostics"
        );
      }
    } catch (e) {
      setError(e?.message || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setDiagnostics(null);
      setError(null);
      setTab(0);
      setTakeOverOpen(false);
      setTakeOverBusy(false);
      setTakeOverError(null);
      setTakeOverBanner(null);
    }
  }, [open]);

  const handleTakeOverConfirm = useCallback(async () => {
    if (!uploadId) return;
    setTakeOverBusy(true);
    setTakeOverError(null);
    try {
      const res = await adminTakeOverUpload(uploadId);
      setTakeOverOpen(false);
      setTakeOverBanner({
        severity: "success",
        message:
          "You now own this upload. The contributor's session will be evicted on their next poll (~2 s)." +
          (res?.previousOwnerSessionId
            ? ` Previous owner session: ${res.previousOwnerSessionId.slice(0, 8)}…`
            : ""),
      });
      // Refresh drawer for the new sessionId + status.
      await load();
      // Also refresh the top-level cross-device fetch so the admin's In Progress
      // list picks up the taken-over upload immediately (2026-08-06 fix — used
      // to require a full page reload for the synthesize+hydrate middleware to
      // notice). The backend's session_id-union query returns the row under the
      // admin's session, upgradeBackendOnlyCardsMiddleware fires
      // hydratePackageFromBackend, and the card lands in uploadingPackages.
      dispatch(fetchActiveSessions());
    } catch (e) {
      setTakeOverError(
        e?.response?.data?.message || e?.message || "Take-over failed"
      );
    } finally {
      setTakeOverBusy(false);
    }
  }, [uploadId, load, dispatch]);

  const snapshot = diagnostics?.snapshot || null;
  const timeline = useMemo(
    () => (snapshot?.timeline ? [...snapshot.timeline].reverse() : []),
    [snapshot]
  );
  const rowForHealth = detail || initialRow || {};
  const derivedHealth = snapshot?.state?.health || deriveRowHealth(rowForHealth);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "560px", md: "720px" },
          background: "#0f1522",
          color: "#f7fafa",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
        },
      }}
    >
      <DrawerHeader
        uploadId={uploadId}
        contributorId={diagnostics?.contributorId || rowForHealth?.contributorId}
        contributorName={rowForHealth?.contributorName}
        health={derivedHealth}
        sessionReplayUrl={diagnostics?.posthogSessionUrl}
        status={detail?.status || initialRow?.status}
        // Compare the row's current session_id against THIS browser's session
        // id — after a successful takeover they match, and we swap the button
        // to a disabled "Already taken over" state so admins don't fire a
        // no-op claim twice.
        alreadyOwned={
          !!detail?.sessionId && detail.sessionId === getUploadSessionId()
        }
        onTakeOver={() => {
          setTakeOverError(null);
          setTakeOverOpen(true);
        }}
        onClose={onClose}
        onRefresh={load}
        loading={loading}
      />

      {takeOverBanner && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Alert
            severity={takeOverBanner.severity}
            variant="outlined"
            onClose={() => setTakeOverBanner(null)}
            data-testid={`admin-take-over-banner-${uploadId}`}
          >
            {takeOverBanner.message}
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        </Box>
      )}

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 2,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          "& .MuiTab-root": {
            color: "#f7fafaa6",
            fontSize: "12px",
            minHeight: 42,
            textTransform: "none",
          },
          "& .Mui-selected": { color: "#fff" },
        }}
      >
        <Tab label={`Timeline (${timeline.length})`} />
        <Tab label="Network" />
        <Tab label="API requests" />
        <Tab label="Retries" />
        <Tab label="State" />
        <Tab label="Raw JSON" />
      </Tabs>

      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {tab === 0 && <TimelineList entries={timeline} />}
        {tab === 1 && (
          <TimelineList
            entries={timeline.filter((e) => e.event?.startsWith("network_") || e.event === "connection_changed")}
            emptyLabel="No network events captured."
          />
        )}
        {tab === 2 && (
          <TimelineList
            entries={timeline.filter((e) => e.event === "api_request" || e.event === "api_response" || e.event === "api_error")}
            emptyLabel="No API events captured."
          />
        )}
        {tab === 3 && (
          <TimelineList
            entries={timeline.filter((e) => e.event === "retry_started" || e.event === "retry_completed" || e.event === "chunk_failed")}
            emptyLabel="No retries captured."
          />
        )}
        {tab === 4 && (
          <StateSection detail={detail} snapshot={snapshot} diagnostics={diagnostics} />
        )}
        {tab === 5 && <RawJsonSection snapshot={diagnostics} />}
      </Box>

      <Dialog
        open={takeOverOpen}
        onClose={() => (takeOverBusy ? null : setTakeOverOpen(false))}
        data-testid={`admin-take-over-dialog-${uploadId}`}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Take over this upload?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13, mb: 1 }}>
            Your admin session will become the owner of
            {" "}<strong>{detail?.packageName || detail?.directoryName || uploadId}</strong>.
            {" "}The contributor's browser will lose control on its next poll (~2 s) and
            the upload card will disappear from their view.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
            Note: this transfers <em>ownership</em>, not the source files. Once you
            own the upload you can cancel it cleanly or let the server finish
            processing. To actually resume uploading you would need the same files
            on your machine.
          </DialogContentText>
          {takeOverError && (
            <Alert severity="error" sx={{ mt: 1.25, fontSize: 12 }}>
              {takeOverError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTakeOverOpen(false)}
            disabled={takeOverBusy}
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleTakeOverConfirm}
            disabled={takeOverBusy}
            variant="contained"
            color="warning"
            size="small"
            data-testid={`admin-take-over-confirm-${uploadId}`}
          >
            {takeOverBusy ? "Taking over…" : "Take over"}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

const DrawerHeader = ({ uploadId, contributorId, contributorName, health, sessionReplayUrl, status, alreadyOwned, onTakeOver, onClose, onRefresh, loading }) => (
  <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="overline" sx={{ color: "#f7fafa66", letterSpacing: "0.06em" }}>
          Upload diagnostics
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            wordBreak: "break-all",
          }}
        >
          {uploadId}
        </Typography>
        <Typography sx={{ color: "#f7fafaa6", fontSize: 12, mt: 0.5 }}>
          {contributorName || contributorId || "—"}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.75}>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={onRefresh} disabled={loading} sx={{ color: "#f7fafa" }}>
            {loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ color: "#f7fafa" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>

    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
      <Chip
        size="small"
        label={HEALTH_LABEL[health] || health}
        color={HEALTH_SEVERITY[health] || "default"}
        variant="filled"
      />
      {sessionReplayUrl ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon fontSize="small" />}
          href={sessionReplayUrl}
          target="_blank"
          rel="noreferrer"
          sx={{ borderColor: "rgba(255,255,255,0.24)", color: "#f7fafa" }}
        >
          Open Session Replay
        </Button>
      ) : (
        <Tooltip title="No PostHog session id captured for this upload (analytics disabled or captured before wiring).">
          <span>
            <Button size="small" variant="outlined" disabled startIcon={<OpenInNewIcon fontSize="small" />}>
              Open Session Replay
            </Button>
          </span>
        </Tooltip>
      )}
      {alreadyOwned ? (
        <Tooltip title="Your browser session already owns this upload — a second takeover would be a no-op.">
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled
              startIcon={<SwapHorizIcon fontSize="small" />}
              data-testid={`admin-take-over-button-${uploadId}`}
            >
              Already taken over
            </Button>
          </span>
        </Tooltip>
      ) : isAdminTakeoverEligible(status) ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SwapHorizIcon fontSize="small" />}
          onClick={onTakeOver}
          data-testid={`admin-take-over-button-${uploadId}`}
          sx={{ borderColor: "rgba(255,255,255,0.24)", color: "#f7fafa" }}
        >
          Take over upload
        </Button>
      ) : (
        <Tooltip title="Takeover is only available while the upload is in an active status (paused, uploading, assembling, etc.). Terminal uploads have no session to claim.">
          <span>
            <Button size="small" variant="outlined" disabled startIcon={<SwapHorizIcon fontSize="small" />}>
              Take over upload
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  </Box>
);

const TimelineList = ({ entries, emptyLabel = "No timeline events captured." }) => {
  if (!entries || entries.length === 0) {
    return (
      <Typography sx={{ color: "#f7fafa80", fontSize: 13, py: 3, textAlign: "center" }}>
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack spacing={1} data-testid="admin-diagnostics-timeline">
      {entries.map((entry, i) => (
        <Box
          key={`${entry.timestamp}-${i}`}
          sx={{
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 1.5,
            p: 1.25,
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip
              size="small"
              label={entry.event}
              variant="outlined"
              sx={{ fontSize: 10.5, borderColor: eventColour(entry.event) }}
            />
            <Typography sx={{ color: "#f7fafa80", fontSize: 11 }}>
              {new Date(entry.timestamp).toISOString()}
            </Typography>
          </Stack>
          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <MetadataBlock metadata={entry.metadata} />
          )}
        </Box>
      ))}
    </Stack>
  );
};

const MetadataBlock = ({ metadata }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      color: "#c9d1e0",
      fontSize: 11.5,
      lineHeight: 1.45,
      fontFamily: "SFMono-Regular, Menlo, monospace",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {JSON.stringify(metadata, null, 2)}
  </Box>
);

const StateSection = ({ detail, snapshot, diagnostics }) => {
  if (!snapshot && !detail) {
    return (
      <Typography sx={{ color: "#f7fafa80", fontSize: 13, py: 3, textAlign: "center" }}>
        No state captured yet.
      </Typography>
    );
  }
  const rows = [];
  if (snapshot?.state) {
    Object.entries(snapshot.state).forEach(([k, v]) =>
      rows.push({ group: "Client state", key: k, value: v })
    );
  }
  if (snapshot?.throughput) {
    Object.entries(snapshot.throughput).forEach(([k, v]) =>
      rows.push({ group: "Throughput", key: k, value: v })
    );
  }
  if (snapshot?.environment) {
    Object.entries(snapshot.environment).forEach(([k, v]) =>
      rows.push({ group: "Environment", key: k, value: v })
    );
  }
  if (snapshot?.network) {
    Object.entries(snapshot.network).forEach(([k, v]) =>
      rows.push({ group: "Network at capture", key: k, value: v })
    );
  }
  if (diagnostics) {
    rows.push({ group: "Correlation", key: "uploadSessionId", value: diagnostics.uploadSessionId });
    rows.push({ group: "Correlation", key: "posthogSessionId", value: diagnostics.posthogSessionId });
    rows.push({ group: "Correlation", key: "snapshotCapturedAt", value: diagnostics.snapshotCapturedAt });
  }

  const grouped = rows.reduce((acc, r) => {
    acc[r.group] = acc[r.group] || [];
    acc[r.group].push(r);
    return acc;
  }, {});

  return (
    <Stack spacing={2}>
      {Object.entries(grouped).map(([group, groupRows]) => (
        <Box key={group}>
          <Typography sx={{ color: "#f7fafaa6", fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", mb: 0.75 }}>
            {group}
          </Typography>
          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            {groupRows.map((r, idx) => (
              <Box
                key={r.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "180px 1fr",
                  gap: 1.5,
                  px: 1.5,
                  py: 0.75,
                  fontSize: 12,
                  borderBottom: idx < groupRows.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                }}
              >
                <Typography sx={{ color: "#f7fafa80", fontSize: 12 }}>{r.key}</Typography>
                <Typography
                  sx={{ color: "#f7fafa", fontSize: 12, wordBreak: "break-all", fontFamily: "SFMono-Regular, Menlo, monospace" }}
                >
                  {formatValue(r.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const RawJsonSection = ({ snapshot }) => {
  const [copied, setCopied] = useState(false);
  if (!snapshot) return null;
  const text = JSON.stringify(snapshot, null, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_error) {
      /* ignore */
    }
  };
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          size="small"
          startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={copy}
          sx={{ color: "#f7fafa" }}
        >
          {copied ? "Copied" : "Copy JSON"}
        </Button>
      </Stack>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box
        component="pre"
        sx={{
          m: 0,
          color: "#c9d1e0",
          fontSize: 11.5,
          lineHeight: 1.45,
          fontFamily: "SFMono-Regular, Menlo, monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Box>
    </Stack>
  );
};

const eventColour = (event) => {
  if (!event) return "rgba(255,255,255,0.24)";
  if (event.includes("fail") || event.includes("error") || event === "upload_stuck") return "#ef5350";
  if (event.includes("retry") || event.includes("stall")) return "#ffb74d";
  if (event.startsWith("network_") || event === "connection_changed") return "#4fc3f7";
  if (event === "upload_completed") return "#66bb6a";
  return "rgba(255,255,255,0.24)";
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "number" && value > 1_000_000_000 && value < 3_000_000_000_000) {
    // Looks like a Unix ms timestamp — format as ISO for readability.
    return new Date(value).toISOString();
  }
  return String(value);
};

export default AdminUploadDiagnosticsDrawer;
